import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Sparkles,
  Wand2,
  Languages,
  AlignLeft,
  CheckSquare,
  List,
  FileText,
  ArrowRight,
  Loader2,
  X,
  Copy,
  Check,
  Minus,
  Plus,
  Briefcase,
  MessageCircle,
  Heart,
  Eraser,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { aiApi, WriteAction, SummarizeStyle } from '../lib/api';

interface AIMenuProps {
  selectedText?: string;
  documentContent?: string;
  onInsert?: (text: string) => void;
  onReplace?: (text: string) => void;
  language?: 'en' | 'ar';
}

interface AIAction {
  id: string;
  icon: React.ReactNode;
  label: string;
  labelAr: string;
  action: () => Promise<void>;
  category: 'write' | 'transform' | 'analyze';
}

export default function AIMenu({
  selectedText = '',
  documentContent = '',
  onInsert,
  onReplace,
  language = 'en',
}: AIMenuProps) {
  const { t, i18n } = useTranslation();
  // Extract just 'en' or 'ar' from locale codes like 'en-US'
  const lang: 'en' | 'ar' = i18n.language.startsWith('ar') ? 'ar' : 'en';
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingAction, setLoadingAction] = useState<string | null>(null);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; left: number } | null>(null);

  const textToProcess = selectedText || documentContent;

  // Close on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  // Recompute dropdown position when opened or on viewport changes
  useEffect(() => {
    if (!isOpen) return;
    const updatePosition = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      setMenuPosition({
        top: rect.bottom + 10,
        left: Math.min(rect.left, window.innerWidth - 320),
      });
    };
    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [isOpen]);

  const executeAction = async (actionId: string, apiCall: () => Promise<{ result?: string; summary?: string; translation?: string; answer?: string; actionItems?: string; toc?: string }>) => {
    setLoadingAction(actionId);
    setError(null);
    setIsLoading(true);

    try {
      const response = await apiCall();
      const output = response.result || response.summary || response.translation || response.answer || response.actionItems || response.toc || '';
      setResult(output);
    } catch (err) {
      setError(lang === 'ar' ? 'حدث خطأ. حاول مرة أخرى.' : 'An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
      setLoadingAction(null);
    }
  };

  const actions: AIAction[] = [
    // Writing actions
    {
      id: 'continue',
      icon: <Wand2 className="h-4 w-4" />,
      label: 'Continue writing',
      labelAr: 'متابعة الكتابة',
      category: 'write',
      action: () => executeAction('continue', () => aiApi.write(textToProcess, 'continue', lang)),
    },
    {
      id: 'improve',
      icon: <Sparkles className="h-4 w-4" />,
      label: 'Improve writing',
      labelAr: 'تحسين الكتابة',
      category: 'write',
      action: () => executeAction('improve', () => aiApi.write(textToProcess, 'improve', lang)),
    },
    {
      id: 'fix',
      icon: <Eraser className="h-4 w-4" />,
      label: 'Fix grammar',
      labelAr: 'تصحيح القواعد',
      category: 'write',
      action: () => executeAction('fix', () => aiApi.write(textToProcess, 'fix', lang)),
    },
    {
      id: 'shorter',
      icon: <Minus className="h-4 w-4" />,
      label: 'Make shorter',
      labelAr: 'اختصار',
      category: 'transform',
      action: () => executeAction('shorter', () => aiApi.write(textToProcess, 'shorter', lang)),
    },
    {
      id: 'longer',
      icon: <Plus className="h-4 w-4" />,
      label: 'Make longer',
      labelAr: 'توسيع',
      category: 'transform',
      action: () => executeAction('longer', () => aiApi.write(textToProcess, 'longer', lang)),
    },
    {
      id: 'professional',
      icon: <Briefcase className="h-4 w-4" />,
      label: 'Professional tone',
      labelAr: 'أسلوب مهني',
      category: 'transform',
      action: () => executeAction('professional', () => aiApi.write(textToProcess, 'professional', lang)),
    },
    {
      id: 'casual',
      icon: <MessageCircle className="h-4 w-4" />,
      label: 'Casual tone',
      labelAr: 'أسلوب عفوي',
      category: 'transform',
      action: () => executeAction('casual', () => aiApi.write(textToProcess, 'casual', lang)),
    },
    {
      id: 'friendly',
      icon: <Heart className="h-4 w-4" />,
      label: 'Friendly tone',
      labelAr: 'أسلوب ودود',
      category: 'transform',
      action: () => executeAction('friendly', () => aiApi.write(textToProcess, 'friendly', lang)),
    },
    {
      id: 'translate',
      icon: <Languages className="h-4 w-4" />,
      label: lang === 'ar' ? 'Translate to English' : 'Translate to Arabic',
      labelAr: lang === 'ar' ? 'ترجمة للإنجليزية' : 'ترجمة للعربية',
      category: 'transform',
      action: () => executeAction('translate', () => 
        aiApi.translate(textToProcess, lang === 'ar' ? 'ar' : 'en', lang === 'ar' ? 'en' : 'ar')
      ),
    },
    {
      id: 'summarize',
      icon: <AlignLeft className="h-4 w-4" />,
      label: 'Summarize',
      labelAr: 'تلخيص',
      category: 'analyze',
      action: () => executeAction('summarize', () => aiApi.summarize(textToProcess, 'paragraph', lang)),
    },
    {
      id: 'bullets',
      icon: <List className="h-4 w-4" />,
      label: 'Bullet points',
      labelAr: 'نقاط رئيسية',
      category: 'analyze',
      action: () => executeAction('bullets', () => aiApi.summarize(textToProcess, 'bullets', lang)),
    },
    {
      id: 'action-items',
      icon: <CheckSquare className="h-4 w-4" />,
      label: 'Extract action items',
      labelAr: 'استخراج المهام',
      category: 'analyze',
      action: () => executeAction('action-items', () => aiApi.extractActionItems(textToProcess, lang)),
    },
    {
      id: 'toc',
      icon: <FileText className="h-4 w-4" />,
      label: 'Table of contents',
      labelAr: 'جدول المحتويات',
      category: 'analyze',
      action: () => executeAction('toc', () => aiApi.generateToc(textToProcess, lang)),
    },
  ];

  const handleCopy = () => {
    if (result) {
      navigator.clipboard.writeText(result);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleInsert = () => {
    if (result && onInsert) {
      onInsert(result);
      setResult(null);
      setIsOpen(false);
    }
  };

  const handleReplace = () => {
    if (result && onReplace) {
      onReplace(result);
      setResult(null);
      setIsOpen(false);
    }
  };

  const categories = [
    { id: 'write', label: lang === 'ar' ? 'كتابة' : 'Write' },
    { id: 'transform', label: lang === 'ar' ? 'تحويل' : 'Transform' },
    { id: 'analyze', label: lang === 'ar' ? 'تحليل' : 'Analyze' },
  ];

  return (
    <div ref={menuRef} className="relative">
      {/* Trigger button */}
      <button
        ref={buttonRef}
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm font-medium',
          'bg-gradient-to-r from-violet-500 to-purple-600 text-white',
          'hover:from-violet-600 hover:to-purple-700 transition-all',
          'shadow-sm hover:shadow-md',
          isOpen && 'ring-2 ring-purple-300'
        )}
      >
        <Sparkles className="h-4 w-4" />
        <span>AI</span>
      </button>

      {/* Dropdown menu */}
      {isOpen && menuPosition && (
        <div
          className="fixed z-[100] w-72 max-h-[70vh] overflow-y-auto rounded-xl border bg-popover shadow-xl animate-scale-in"
          style={{ top: menuPosition.top, left: menuPosition.left }}
        >
          {/* Result view */}
          {result ? (
            <div className="p-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-purple-500" />
                  <span className="text-sm font-medium">
                    {lang === 'ar' ? 'نتيجة الذكاء الاصطناعي' : 'AI Result'}
                  </span>
                </div>
                <button onClick={() => setResult(null)} className="btn-icon h-6 w-6">
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>

              <div className="rounded-lg border bg-muted/50 p-3 text-sm max-h-60 overflow-y-auto mb-3" dir="auto">
                {result}
              </div>

              <div className="flex gap-2">
                <button
                  onClick={handleCopy}
                  className="btn-ghost flex-1 text-xs"
                >
                  {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                  {copied ? (lang === 'ar' ? 'تم النسخ' : 'Copied') : (lang === 'ar' ? 'نسخ' : 'Copy')}
                </button>
                {onInsert && (
                  <button onClick={handleInsert} className="btn-primary flex-1 text-xs">
                    {lang === 'ar' ? 'إدراج' : 'Insert'}
                  </button>
                )}
                {onReplace && selectedText && (
                  <button onClick={handleReplace} className="btn-secondary flex-1 text-xs">
                    {lang === 'ar' ? 'استبدال' : 'Replace'}
                  </button>
                )}
              </div>
            </div>
          ) : error ? (
            <div className="p-4">
              <div className="rounded-lg bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive mb-3">
                {error}
              </div>
              <button onClick={() => setError(null)} className="btn-ghost w-full text-xs">
                {lang === 'ar' ? 'المحاولة مرة أخرى' : 'Try again'}
              </button>
            </div>
          ) : (
            <div className="p-2">
              {/* No text warning */}
              {!textToProcess && (
                <div className="px-3 py-2 text-xs text-muted-foreground text-center mb-2">
                  {lang === 'ar' ? 'حدد نصاً أو اكتب محتوى أولاً' : 'Select text or write content first'}
                </div>
              )}

              {/* Actions by category */}
              {categories.map((category) => (
                <div key={category.id} className="mb-2">
                  <div className="px-2 py-1 text-xs font-medium text-muted-foreground">
                    {category.label}
                  </div>
                  {actions
                    .filter((a) => a.category === category.id)
                    .map((action) => (
                      <button
                        key={action.id}
                        onClick={action.action}
                        disabled={!textToProcess || isLoading}
                        className={cn(
                          'menu-item w-full',
                          loadingAction === action.id && 'bg-secondary',
                          !textToProcess && 'opacity-50 cursor-not-allowed'
                        )}
                      >
                        {loadingAction === action.id ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          action.icon
                        )}
                        <span>{lang === 'ar' ? action.labelAr : action.label}</span>
                      </button>
                    ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
